import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import { useProject } from "../context/ProjectContext.jsx";
import { useIdwiz } from "../hooks/useIdwiz.js";
import { hasToken, retrieveToken } from "../services/tokenVault.js";
import {
  clearTenantHealthSnapshot,
  setTenantHealthSnapshot,
} from "../utils/tenantHealthStore.js";
import Topbar from "../components/Topbar.jsx";
import CredentialHealthStrip from "../components/credentials/CredentialHealthStrip.jsx";
import ApprovalsDrawer from "../components/approvals/ApprovalsDrawer.jsx";
import FrameworksModal from "../components/compliance/FrameworksModal.jsx";
import Roadmap from "../components/Roadmap.jsx";
import Dashboard from "./Dashboard.jsx";
import ResumeProjectModal from "../components/ResumeProjectModal.jsx";
import Skeleton from "../components/ui/Skeleton.jsx";
import TokenUsageBadge from "../components/dev/TokenUsageBadge.jsx";
import ConnectAuth0Modal from "../components/ConnectAuth0Modal.jsx";
import ConnectEntraModal from "../components/ConnectEntraModal.jsx";
import ConnectOryModal from "../components/ConnectOryModal.jsx";
import ConnectOrySelfHostedModal from "../components/ConnectOrySelfHostedModal.jsx";
import {
  isCredentiallessWorkspace,
  isEntraProject,
  isOktaProject,
  isOrySelfHostedProject,
  shouldSkipResumeModal,
} from "../utils/projectProvider.js";

function sameOrgHostname(a, b) {
  if (!a || !b) return false;
  try {
    const na = new URL(/^https?:\/\//i.test(String(a)) ? String(a) : `https://${a}`).hostname.toLowerCase();
    const nb = new URL(/^https?:\/\//i.test(String(b)) ? String(b) : `https://${b}`).hostname.toLowerCase();
    return na === nb;
  } catch {
    return false;
  }
}

function ProjectLoadingSkeleton() {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        minHeight: 0,
        background: "var(--bg-base)",
      }}
    >
      <aside
        style={{
          width: "280px",
          flexShrink: 0,
          borderRight: "1px solid var(--border)",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <Skeleton height={20} width="55%" borderRadius={4} />
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={56} width="100%" borderRadius={6} />
        ))}
      </aside>
      <div style={{ flex: 1, padding: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Skeleton height={28} width="40%" borderRadius={4} />
        <Skeleton height={120} width="100%" borderRadius={8} />
        <Skeleton height={200} width="100%" borderRadius={8} />
      </div>
      {import.meta.env.DEV ? <TokenUsageBadge /> : null}
    </div>
  );
}

export default function ProjectWorkspace() {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiToken: oktaToken, orgUrl: oktaOrgUrl, clearSession, setSession } = useOktaSession();
  const {
    project,
    apiToken,
    setApiToken,
    disconnect,
    loadProject,
    isLoading,
    auditData,
    setAuditData,
    setDriftData,
    updateRoadmapItem,
    setProject,
  } = useProject();
  const idwiz = useIdwiz();
  const [me, setMe] = useState(null);
  const [selectedRoadmapId, setSelectedRoadmapId] = useState(null);
  const [reconnectBanner, setReconnectBanner] = useState(null);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [frameworksOpen, setFrameworksOpen] = useState(false);
  const [connectAuth0Open, setConnectAuth0Open] = useState(false);
  const [connectEntraOpen, setConnectEntraOpen] = useState(false);
  const [connectOryOpen, setConnectOryOpen] = useState(false);
  const [connectOrySelfHostedOpen, setConnectOrySelfHostedOpen] = useState(false);
  const consumed = useRef(false);

  const pendingApprovalCount = useMemo(() => {
    const a = project?.pendingApprovals;
    if (!Array.isArray(a)) return 0;
    return a.filter((x) => x && x.status === "pending").length;
  }, [project?.pendingApprovals]);

  useEffect(() => {
    let c = false;
    fetch("/api/me", { credentials: "include" })
      .then((r) => r.json())
      .then((j) => {
        if (!c) setMe(j);
      })
      .catch(() => {
        if (!c) setMe(null);
      });
    return () => {
      c = true;
    };
  }, []);

  /** Keep Okta session snapshot aligned with vault for this project's org (useIdwiz / generateArchitectReply). */
  useEffect(() => {
    if (!project?.org?.url) return;
    const url = project.org.url;
    const v = retrieveToken(url);
    if (v) {
      setSession({ orgUrl: url, apiToken: v });
      return;
    }
    if (oktaToken && sameOrgHostname(url, oktaOrgUrl)) {
      setApiToken(oktaToken, url);
      setSession({ orgUrl: url, apiToken: oktaToken });
    }
  }, [project?.org?.url, project?.projectId, oktaToken, oktaOrgUrl, setSession, setApiToken]);

  /** If memo has not yet picked up vault (same tick), nudge after read from sessionStorage. */
  useEffect(() => {
    if (!project?.org?.url) return;
    const vaultToken = retrieveToken(project.org.url);
    if (vaultToken && !apiToken) {
      setApiToken(vaultToken, project.org.url);
    }
  }, [project?.org?.url, apiToken, setApiToken]);

  useEffect(() => {
    const b = location.state?.reconnectBanner;
    if (!b) return;
    setReconnectBanner(String(b));
    navigate(location.pathname, { replace: true, state: {} });
    const t = window.setTimeout(() => setReconnectBanner(null), 2000);
    return () => window.clearTimeout(t);
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (consumed.current) return;
    const st = location.state;
    if (st?.audit) setAuditData(st.audit);
    if (st?.drift !== undefined && st?.drift !== null) setDriftData(st.drift);
    if (st?.audit || (st?.drift !== undefined && st?.drift !== null)) {
      consumed.current = true;
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, setAuditData, setDriftData]);

  useEffect(() => {
    if (auditData || !project?.auditSnapshot) return;
    setAuditData(project.auditSnapshot);
  }, [auditData, project?.auditSnapshot, setAuditData]);

  const prevRoadmapSelectionRef = useRef(null);
  /** When the user selects a roadmap item, stop the spinner on other in_progress rows. */
  useEffect(() => {
    if (!updateRoadmapItem || !Array.isArray(project?.roadmap?.items)) return;
    if (prevRoadmapSelectionRef.current === selectedRoadmapId) return;
    prevRoadmapSelectionRef.current = selectedRoadmapId;
    if (!selectedRoadmapId) return;
    for (const it of project.roadmap.items) {
      const st = String(it.status ?? "")
        .toLowerCase()
        .replace(/\s+/g, "_");
      if (st === "in_progress" && String(it.id) !== String(selectedRoadmapId)) {
        void updateRoadmapItem(it.id, { status: "available" });
      }
    }
  }, [selectedRoadmapId, project?.projectId, updateRoadmapItem, project]);

  const isEntra = isEntraProject(project);
  const credentialless = isCredentiallessWorkspace(project);
  const showOktaChrome = isOktaProject(project);
  const skipResume = shouldSkipResumeModal(project);
  const tokenAvailable = skipResume
    ? true
    : Boolean(apiToken || oktaToken || (project?.org?.url && hasToken(project.org.url)));
  const needsResume = Boolean(project && !skipResume && !tokenAvailable && !isLoading);

  const handleDisconnect = useCallback(async () => {
    clearTenantHealthSnapshot();
    await idwiz.endSession?.();
    disconnect();
    clearSession();
    navigate("/");
  }, [idwiz, disconnect, clearSession, navigate]);

  /** Quick Okta health snapshot for Architect tenantContext on first messages. */
  useEffect(() => {
    if (shouldSkipResumeModal(project)) return;
    const ou = project?.org?.url;
    if (!ou) return;
    const tok = apiToken || oktaToken || retrieveToken(ou);
    if (!tok) {
      clearTenantHealthSnapshot();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/idp/okta/health-check", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orgUrl: ou, apiToken: tok }),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data?.ok) setTenantHealthSnapshot(ou, data);
        else if (!cancelled) clearTenantHealthSnapshot();
      } catch {
        if (!cancelled) clearTenantHealthSnapshot();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project?.org?.url, project?.projectId, apiToken, oktaToken]);

  const userEmail = me?.user?.email ?? "";

  if (isLoading && !project) {
    return <ProjectLoadingSkeleton />;
  }

  if (!isLoading && !project) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "var(--bg-base)",
          padding: "24px",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "360px" }}>
          <p className="text-secondary" style={{ marginBottom: "16px" }}>
            Project not found or you don&apos;t have access.
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            style={{
              background: "var(--accent)",
              color: "#0a0f1a",
              border: "none",
              borderRadius: "6px",
              padding: "10px 16px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  if (needsResume && project) {
    return (
      <ResumeProjectModal
        projectSummary={{
          projectId: project.projectId,
          orgUrl: project.org?.url,
          orgName: project.org?.name,
          status: project.status,
          updatedAt: project.updatedAt,
          roadmapProgress: project.roadmap?.items?.length
            ? {
                complete: project.roadmap.items.filter(
                  (i) => i?.status === "complete" || i?.completed === true,
                ).length,
                total: project.roadmap.items.length,
              }
            : { complete: 0, total: 0 },
        }}
        onClose={() => navigate("/")}
        onDone={() => loadProject?.()}
      />
    );
  }

  const roadmapItems = project?.roadmap?.items ?? [];

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "var(--bg-base)",
      }}
    >
      {reconnectBanner ? (
        <div
          className="text-accent"
          style={{
            fontSize: "13px",
            textAlign: "center",
            padding: "8px",
            background: "var(--bg-surface)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          Reconnected to {reconnectBanner}
        </div>
      ) : null}
      {import.meta.env.DEV ? <TokenUsageBadge /> : null}
      <Topbar
        project={project}
        userEmail={userEmail}
        onDisconnect={handleDisconnect}
        pendingApprovalCount={showOktaChrome ? pendingApprovalCount : 0}
        onOpenApprovals={showOktaChrome ? () => setApprovalsOpen(true) : undefined}
        onOpenFrameworks={() => setFrameworksOpen(true)}
        onRenamed={(updated) => {
          if (updated) setProject(updated);
        }}
        readOnlyAssessment={credentialless}
      />
      {project?.credentialHealth?.hasCredentials ? (
        <div
          style={{
            flexShrink: 0,
            padding: "6px 20px",
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <CredentialHealthStrip health={project.credentialHealth} compact />
        </div>
      ) : null}
      <div style={{ flex: 1, display: "flex", minHeight: 0, minWidth: 0 }}>
        {showOktaChrome ? (
          <Roadmap
            items={roadmapItems}
            selectedId={selectedRoadmapId}
            onSelectItem={(item) => {
              setSelectedRoadmapId(item?.id != null ? String(item.id) : null);
              const title = item?.title || "item";
              void idwiz.sendArchitectMessage(`[PIVOT: User selected roadmap item "${title}"]`);
            }}
            onMarkComplete={(itemId) => {
              void updateRoadmapItem(itemId, {
                status: "complete",
                completedAt: new Date().toISOString(),
              });
            }}
          />
        ) : null}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Dashboard
            idwiz={idwiz}
            isEntraProject={isEntra}
            onOpenAuth0Connect={() => setConnectAuth0Open(true)}
            onOpenEntraConnect={() => setConnectEntraOpen(true)}
            onOpenOryConnect={
              isOrySelfHostedProject(project) ? undefined : () => setConnectOryOpen(true)
            }
            onOpenOrySelfHostedConfig={
              isOrySelfHostedProject(project) ? () => setConnectOrySelfHostedOpen(true) : undefined
            }
          />
        </div>
      </div>
      {showOktaChrome ? (
        <ApprovalsDrawer
          open={approvalsOpen}
          onClose={() => setApprovalsOpen(false)}
          project={project}
          loadProject={loadProject}
          onSystemNotice={(text) => {
            if (!text) return;
            void idwiz.sendArchitectMessage(text);
          }}
        />
      ) : null}
      <FrameworksModal
        open={frameworksOpen}
        onClose={() => setFrameworksOpen(false)}
        project={project}
        onSaved={async () => {
          await loadProject?.();
          void idwiz.sendArchitectMessage(
            "[SYSTEM: Project compliance scope updated. Re-run the org audit to refresh prioritization and citations.]",
          );
        }}
      />
      <ConnectAuth0Modal
        open={connectAuth0Open}
        onClose={() => setConnectAuth0Open(false)}
        project={project}
        onConnected={() => {
          void loadProject?.();
        }}
      />
      <ConnectEntraModal
        open={connectEntraOpen}
        onClose={() => setConnectEntraOpen(false)}
        project={project}
        onConnected={() => {
          void loadProject?.();
        }}
      />
      {!isOrySelfHostedProject(project) ? (
        <ConnectOryModal
          open={connectOryOpen}
          onClose={() => setConnectOryOpen(false)}
          project={project}
          onConnected={() => {
            void loadProject?.();
            void idwiz.sendArchitectMessage(
              "[SYSTEM: Ory Project API key updated. Re-run assessment from the command panel (Run new assessment) to refresh findings.]",
            );
          }}
        />
      ) : (
        <ConnectOrySelfHostedModal
          open={connectOrySelfHostedOpen}
          onClose={() => setConnectOrySelfHostedOpen(false)}
          project={project}
          onSaved={() => {
            void loadProject?.();
            void idwiz.sendArchitectMessage(
              "[SYSTEM: Self-hosted Ory inputs updated. Re-run assessment from the command panel to refresh findings on the same project.]",
            );
          }}
        />
      )}
    </div>
  );
}
